/* eslint-disable @typescript-eslint/no-non-null-assertion */
import classNames from 'classnames';
import Button from 'components/base/Button';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as formik from 'formik';
import * as yup from 'yup';

const totalInputLength = 6;

const TwoFAForm = ({ layout }: { layout?: 'simple' | 'card' | 'split' }) => {
  const navigate = useNavigate();

  const [otp, setOtp] = useState('');
  const { Formik } = formik;

  const schema = yup.object().shape({
    otp: yup
      .string()
      .length(totalInputLength, `OTP must be ${totalInputLength} digits`)
      .required('OTP is required')
  });

  const authEmail = localStorage.getItem('auth-email');
  useEffect(() => {
    if (!authEmail) {
      navigate('/auth/login');
    }
  }, [authEmail, navigate]);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ): void => {
    const { value } = e.target;

    if (value) {
      [...value].slice(0, totalInputLength).forEach((char, charIndex) => {
        if (inputRefs.current && inputRefs.current[index + charIndex]) {
          inputRefs.current[index + charIndex]!.value = char;
          inputRefs.current[index + charIndex + 1]?.focus();
        }
      });
    } else {
      inputRefs.current[index]!.value = '';
      inputRefs.current[index - 1]?.focus();
    }

    const updatedOtp = inputRefs.current.reduce(
      (acc, input) => acc + (input?.value || ''),
      ''
    );
    setOtp(updatedOtp);
  };

  const onSubmit = async (values: any) => {
    const formData = values;
    formData.email = authEmail;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/auth/verify-otp`,
        formData
      );

      if (response.data['status'] == false) {
        toast.error(response.data['error']['message']);
        return false;
      }

      localStorage.setItem(
        'user',
        JSON.stringify(response.data['data']['user'])
      );
      localStorage.setItem(
        'refreshToken',
        response.data['data']['refreshToken']
      );
      localStorage.setItem('token', response.data['data']['token']);
      localStorage.setItem('isAuthenticated', 'true');
      navigate(`/dashboard`);
    } catch (error) {
      console.error('Error occurred:', error);
    }
  };

  const maskEmailFixed = (email: any, left = 3, right = 3, mask = '****') => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain) return email;
    if (local.length <= left + right) return email;
    return `${local.slice(0, left)}${mask}${local.slice(-right)}@${domain}`;
  };

  return (
    <Formik
      initialValues={{ otp: '' }}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, setFieldValue, values, errors, touched }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <div className={classNames({ 'px-xxl-5': !(layout === 'split') })}>
            <div
              className={classNames('text-center', {
                'mb-6': !(layout === 'split')
              })}
            >
              <h4 className="text-body-highlight">
                Enter the verification code
              </h4>
              <p className="text-body-tertiary mb-0">
                An email containing a 6-digit verification code has been sent to
                the email address - {maskEmailFixed(authEmail)}
              </p>
              <p></p>
              <div className="verification-form">
                <div className="d-flex align-items-center gap-2 mb-3">
                  {Array(totalInputLength)
                    .fill('')
                    .map((_, index) => (
                      <React.Fragment key={index}>
                        <Form.Control
                          ref={(el: HTMLInputElement) => {
                            inputRefs.current[index] = el;
                          }}
                          className="px-2 text-center"
                          type="number"
                          maxLength={1}
                          value={values.otp[index] || ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const val = e.target.value
                              .replace(/\D/g, '')
                              .slice(0, 1);
                            // Build new OTP string
                            const otpArr = values.otp.split('');
                            otpArr[index] = val;
                            const newOtp = otpArr
                              .join('')
                              .slice(0, totalInputLength);
                            setFieldValue('otp', newOtp);
                            setOtp(newOtp);
                            // Move focus
                            if (val && inputRefs.current[index + 1]) {
                              inputRefs.current[index + 1]?.focus();
                            }
                          }}
                          onKeyDown={e => {
                            if (
                              e.key === 'Backspace' &&
                              !values.otp[index] &&
                              inputRefs.current[index - 1]
                            ) {
                              inputRefs.current[index - 1]?.focus();
                            }
                          }}
                          isInvalid={touched.otp && !!errors.otp}
                        />
                        {index === 2 && <span>-</span>}
                      </React.Fragment>
                    ))}
                </div>
                {touched.otp && errors.otp && (
                  <div className="text-danger mb-2">{errors.otp}</div>
                )}
                <br />
                <br />
                <Button
                  variant="primary"
                  className="w-100 mb-5"
                  type="submit"
                  disabled={values.otp.length < totalInputLength}
                >
                  Verify
                </Button>
                <Link to="#!" className="fs-9">
                  Didn’t receive the code?
                </Link>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default TwoFAForm;
